import styled, { css } from 'styled-components';
import { formatRelativeDate } from 'utils';
import {
  DetailListItem,
  DetailListItemDate,
  DetailListItemLink,
  DetailListItemExternalLink,
} from 'ui/Details';
import truncate from 'lodash/truncate';
import IcAuction from 'components/icons/Auction';
import IcChat from 'components/icons/Chat';
import IcNotification from 'components/icons/Notification';
import IcReport from 'components/icons/Report';
import IcPiggyBank from 'components/icons/PiggyBank';
import { MdInsertDriveFile } from 'react-icons/md';
import { AttachmentLinkIndicator } from 'ui';
import IcDocument from 'components/icons/Document';
import IcSupport from 'components/icons/Support';
import ReportTag from 'components/ReportTag';
import Avatar from 'components/Avatar';
import { Media, MediaImage, MediaBody } from 'ui';
import { MessageAuthor, MessageExcerpt } from 'components/ConversationsSidebar';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getCoownershipDocument, getContractDocument } from 'api/contracts';
import { useLocation } from 'react-router';
import { capitalize } from '@tymate/sergic';

const ChunkTitle = styled.h2`
  font-size: ${({ theme }) => theme.size(0)};
  font-weight: 600;
  margin: 0 0 ${({ theme }) => theme.spacing()};

  > svg {
    margin-right: ${({ theme }) => theme.spacing(0.25)};
  }
`;

const ChunkSubtitle = styled.h3`
  margin: ${({ theme }) => theme.spacing(0.5)} 0;
  font-size: ${({ theme }) => theme.size(1)};
  font-weight: 700;

  ${props =>
    props.isDeleted &&
    css`
      color: ${({ theme }) => theme.textLight};
      text-decoration: line-through;
    `};
`;

const ChunkBody = styled.p`
  margin-top: 0;
  margin-bottom: 0;
`;

const ReportFeedChunk = ({ data, createdAt, displayName, ...rest }) => {
  const isResolved = Boolean(data.resolutionTime);
  const { t } = useTranslation();
  const title = isResolved
    ? t('notifications_type:resolved_report')
    : t('reportForm:new');
  return (
    <DetailListItemLink
      variant="raw"
      to={`/mes-echanges/signalements/${data.id}`}
    >
      <DetailListItemDate variant="small">
        {formatRelativeDate(createdAt)}
      </DetailListItemDate>
      <ChunkTitle>
        <IcReport /> {title}
      </ChunkTitle>
      {!isResolved && Boolean(data.category) && (
        <ReportTag
          {...data.category}
          displayName={t(`report_categories:${data.category.id}`)}
        />
      )}
      <ChunkSubtitle isDeleted={isResolved}>{data.displayName}</ChunkSubtitle>
    </DetailListItemLink>
  );
};

const ReportEventFeedChunk = ({ data, createdAt, displayName }) => (
  <DetailListItemLink
    variant="raw"
    to={`/mes-echanges/signalements/${data.report.id}`}
  >
    <DetailListItemDate variant="small">
      {formatRelativeDate(createdAt)}
    </DetailListItemDate>
    <ChunkTitle>
      <IcReport /> {displayName}
    </ChunkTitle>
    <ChunkSubtitle>{data.report.displayName}</ChunkSubtitle>
    <ChunkBody>{data.description}</ChunkBody>
  </DetailListItemLink>
);

const DecisionFeedChunk = ({ data, createdAt, displayName }) => (
  <DetailListItemLink variant="raw" to={`/decisions/${data.uuid}`}>
    <DetailListItemDate variant="small">
      {formatRelativeDate(createdAt)}
    </DetailListItemDate>
    <ChunkTitle>
      <IcAuction /> {displayName}
    </ChunkTitle>
    <ChunkSubtitle>{data?.report?.displayName}</ChunkSubtitle>
    <ChunkBody>{data?.description}</ChunkBody>
  </DetailListItemLink>
);

const BillerFeedChunk = ({ data, createdAt, displayName }) => (
  <DetailListItemLink variant="raw" to={`/mes-comptes/facturiers/${data.uuid}`}>
    <DetailListItemDate variant="small">
      {formatRelativeDate(createdAt)}
    </DetailListItemDate>
    <ChunkTitle>
      <IcPiggyBank /> {displayName}
    </ChunkTitle>
    <ChunkBody>{data?.description}</ChunkBody>
  </DetailListItemLink>
);

const WorkOrderFeedChunk = ({ data, createdAt, displayName }) => (
  <DetailListItemLink variant="raw" as="div">
    <DetailListItemDate variant="small">
      {formatRelativeDate(createdAt)}
    </DetailListItemDate>
    <ChunkTitle>
      <IcNotification /> {displayName}
    </ChunkTitle>
    <ChunkSubtitle>{data.title}</ChunkSubtitle>
    <ChunkBody>{data.description}</ChunkBody>
  </DetailListItemLink>
);

const NotificationFeedChunk = ({ data, createdAt, displayName }) => {
  const location = useLocation();

  return (
    <DetailListItemLink
      variant="raw"
      to={`/actualites/${data.id}${location.search}`}
    >
      <DetailListItemDate variant="small">
        {formatRelativeDate(createdAt)}
      </DetailListItemDate>
      <ChunkTitle>
        <IcNotification /> {displayName}
      </ChunkTitle>
      <ChunkSubtitle>{data.title}</ChunkSubtitle>
      <ChunkBody>
        {truncate(data.message, { length: 280, omission: '…' })}
      </ChunkBody>
    </DetailListItemLink>
  );
};

const ConversationFeedChunk = ({
  data,
  createdAt,
  displayName,
  kind,
  ...rest
}) => {
  const { t } = useTranslation('jobSlang');
  return (
    <DetailListItemLink
      variant="raw"
      to={`/mes-echanges/mes-messages/${data.id}`}
    >
      <DetailListItemDate variant="small">
        {formatRelativeDate(createdAt)}
      </DetailListItemDate>
      <ChunkTitle>
        <IcChat /> {displayName}
      </ChunkTitle>
      <ChunkSubtitle>{data.preview}</ChunkSubtitle>
      <Media>
        <MediaImage>
          <Avatar {...data.author} small />
        </MediaImage>
        <MediaBody tight>
          <MessageAuthor>{capitalize(data.author.displayName)}</MessageAuthor>
          <MessageExcerpt>{t(data.author.displayPlaceRole)}</MessageExcerpt>
        </MediaBody>
      </Media>
    </DetailListItemLink>
  );
};

const ContractDocumentFeedChunk = ({ data, createdAt, displayName }) => {
  const { data: contractDocumentData } = useQuery(
    ['contractDocument', data?.id],
    () => getContractDocument(data?.id),
  );

  return (
    <DetailListItemExternalLink
      href={contractDocumentData?.data?.fileUrl}
      target="_blank"
      rel="noopener noreferer nofollow"
    >
      <DetailListItemDate variant="small">
        {formatRelativeDate(createdAt)} 
      </DetailListItemDate>
      <ChunkTitle>
        <IcDocument /> {displayName}
      </ChunkTitle>
      <ChunkSubtitle>{displayName}</ChunkSubtitle>
      <AttachmentLinkIndicator>
        <MdInsertDriveFile /> {data.displayName || data.documentName}
      </AttachmentLinkIndicator>
    </DetailListItemExternalLink>
  );
};

const CoownershipDocumentFeedChunk = ({ data, createdAt, displayName }) => {
  const { data: coownershipDocumentData } = useQuery(
    ['coownershipDocument', data?.id],
    () => getCoownershipDocument(data?.id),
  );

  return (
    <DetailListItemExternalLink
      href={coownershipDocumentData?.data?.fileUrl}
      target="_blank"
      rel="noopener noreferer nofollow"
    >
      <DetailListItemDate variant="small">
        {formatRelativeDate(createdAt)} 
      </DetailListItemDate>
      <ChunkTitle>
        <IcDocument /> {displayName}
      </ChunkTitle>
      <ChunkSubtitle>{displayName}</ChunkSubtitle>
      <AttachmentLinkIndicator>
        <MdInsertDriveFile /> {data.displayName || data.documentName}
      </AttachmentLinkIndicator>
    </DetailListItemExternalLink>
  );
};

const ZendeskTicketChunk = ({ data, createdAt, displayName }) => (
  <DetailListItemLink variant="raw" to={`/mes-echanges/support/${data.id}`}>
    <DetailListItemDate variant="small">
      {formatRelativeDate(createdAt)}
    </DetailListItemDate>
    <ChunkTitle>
      <IcSupport /> {displayName}
    </ChunkTitle>
    <ChunkSubtitle>{data.title}</ChunkSubtitle>

    <Media>
      <MediaImage>
        <Avatar small />
      </MediaImage>
      <MediaBody tight>
        <MessageAuthor>{capitalize(data.agentName)}</MessageAuthor>
        {/* <MessageExcerpt>{data.author.displayPlaceRole}</MessageExcerpt> */}
      </MediaBody>
    </Media>
  </DetailListItemLink>
);

const FeedChunk = ({ kind, isHighlighted, ...feedChunk }) => {
  switch (kind) {
    case 'report':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <ReportFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'report_event':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <ReportEventFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'notification':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <NotificationFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'conversations':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <ConversationFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'work_order':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <WorkOrderFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'contract_document':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <ContractDocumentFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'coownership_document':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <CoownershipDocumentFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'zendesk_ticket':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <ZendeskTicketChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'decision':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <DecisionFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    case 'biller':
      return (
        <DetailListItem isHighlighted={isHighlighted}>
          <BillerFeedChunk {...feedChunk} />
        </DetailListItem>
      );
    default:
      return null;
  }
};

export default FeedChunk;
